import { yupResolver } from '@hookform/resolvers/yup';
import { ChevronLeftIcon, EnterIcon, ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { Badge, Button, Callout, Card, Flex, Separator, Spinner } from '@radix-ui/themes';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useMutation } from 'react-query';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import SelectComponent from '../../components/inputs/Select/SelectComponent';
import TextFieldComponent from '../../components/inputs/Textfield/TextfieldComponent';
import { useGetCoursesQuery } from '../../hooks/useGetCoursesQuery';
import { useGetCurrentAcademicYearQuery } from '../../hooks/useGetCurrentAcademicYearQuery';
import { useGetDepartamentsQuery } from '../../hooks/useGetDepartamentsQuery';
import { useGetFacultiesQuery } from '../../hooks/useGetFacultiesQuery';
import { RootState } from '../../redux/store';
import { routes } from '../../routes';
import { updateClass } from '../../services/school/class/updateClass';
import { ClassType } from '../../types/Class';
import { CreateClassFormValidation } from './CreateClassFormValidation';
import RadioGroupComponent from '../../components/inputs/RadioGroup/RadioGroupComponent';
import { useGetUniversitiesQuery } from '../../hooks/useGetUniversitiesQuery';
import CheckboxComponent from '../../components/inputs/Checkbox/CheckboxComponent';
import DateFieldComponent from '../../components/inputs/Datefield/DatefieldComponent';
import { stringToBoolean } from '../../utils/utilities';
import { useEffect } from 'react';

export const UpdateClass = () => {
  const navigate = useNavigate();
  const mutation = useMutation({ mutationFn: updateClass, mutationKey: 'updateClass' });
  const { data: currentAcademicYear, isLoading: isLoadingCurrentAcademicYear, isError: errorCurrentAcademicYear } = useGetCurrentAcademicYearQuery();
  const classData = useSelector((state: RootState) => state.classes);

  const defaultValues: ClassType = classData;
  const { control, handleSubmit, setValue, getValues, formState: { errors } ,watch} = useForm<ClassType>({
    resolver: yupResolver(CreateClassFormValidation),
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldUseNativeValidation: false,
  });

  const { data:universities, isLoading: isLoadingUniversities, isError: errorUniversities, refetch: refetchUniversities } = useGetUniversitiesQuery({ PageNumber: 1, PageSize: 400 });
  const { data: faculties, isLoading: isLoadingFaculties, isError: errorFaculties,refetch:refetchFaculties  } = useGetFacultiesQuery({ PageNumber: 1, PageSize: 400,IdUniversity:getValues('idUniversity')});
  const { data: departaments, isLoading: isLoadingDepartaments, isError: errorDepartaments, refetch: refreshDepartaments } = useGetDepartamentsQuery({ PageNumber: 1, PageSize: 400, IdFaculty: getValues('idFaculty') },
    {
      enabled: !!getValues('idFaculty')
    });
  const { data: courses, isLoading: isLoadingCourses, isError: errorCourses, refetch: refreshCourses } = useGetCoursesQuery({ PageNumber: 1, PageSize: 400, IdDepartament: getValues('idDepartament') },
    {
      enabled: !!getValues('idDepartament')
    });
  
      const generateClassName = () => {
    const courseId = getValues('idCourse');
    const course = courses?.items.find(c => c.id === courseId);
    const level = getValues('level');
    const semester = getValues('semester');
    const regime = getValues('regime');
    const year = currentAcademicYear?.year || new Date().getFullYear();
    if (!course || !level || !semester || !regime || !year){
       setValue('description', '');
       return;
    } 
    const acronym = course.name
      ?.split(' ')
      .filter(word => word.length > 2 && /^[A-ZÁÉÍÓÚÂÊÔÇ]/i.test(word))
      .map(word => word[0])
      .join('')
      .toUpperCase() || 'TURMA';

    const regimeCode = {
      1: 'L', 
      2: 'P', 
      3: 'D'  
    }[Number(regime)] || 'X';
    const className = `${acronym}-${level}${regimeCode}-${year}-S${semester}`;
    setValue('description', className);
  };
    useEffect(() => {
      const subscription = watch((value, { name }) => {
        if (
          ['idCourse', 'level', 'semester', 'regime'].includes(name || '') &&
          value.idCourse &&
          value.level &&
          value.semester &&
          value.regime
        ) {
          generateClassName();
        }
      });
  
      return () => subscription.unsubscribe();
    }, [watch, courses, currentAcademicYear]);
  if (!currentAcademicYear || !currentAcademicYear?.status) {
    return (
        <Flex direction="column">
            <Callout.Root color="red" role="alert">
                <Callout.Icon>
                    <ExclamationTriangleIcon />
                </Callout.Icon>
                <Callout.Text>
                    Por favor active o ano lectivo.{' '}
                    <Link to={routes.academicYears} style={{ color: 'var(--gray-12)' }}>Voltar</Link>
                </Callout.Text>
            </Callout.Root>

        </Flex>
    );
}
  const handleFacultyChange = (idFaculty: string) => {
    setValue('idFaculty', idFaculty);
    refreshDepartaments();
  };
  const handleDepartamentChange = (idDepartament: string) => {
    setValue('idDepartament', idDepartament);
    refreshCourses();
  };

  const onSubmit = handleSubmit(async (data) => {
    try {
      data.idAcademicYear = currentAcademicYear.id;
      try{
        data.regime= Number(data.regime);
      }catch(ex){
          console.error({ex})
      }
      const result = await mutation.mutateAsync(data);
      if (result) {
        toast.success("Turma actualizada");
        navigate(routes.classes);
      }
    } catch (err: any) {
      toast.error(`${mutation.error ?? err?.message}`);
    }
  });

  const handleBack = () => {
    navigate(routes.classes);
  }
  const handleUniversityChange = (id: string) => {
    setValue('idUniversity', id);
    setValue('idFaculty', '');
    setValue('idDepartament', '');
    setValue('idCourse', '');
    refetchFaculties();
  };
    const isUseOwnRulesForAcademicPeriod = stringToBoolean(watch('useOwnRulesForAcademicPeriod'));
  
  return (
    <form onSubmit={onSubmit} noValidate>
      <Card size='1'>
        <Flex direction='column' gap='4'>
     
          <Flex align="center">
            <Badge variant="surface" >{getValues('description')}</Badge>
            <Separator orientation="horizontal" />
            <Badge variant="outline" >{classData.academicYear}</Badge>
          </Flex>

          <Flex gap="8">
            <TextFieldComponent name="level" label="Ano" control={control} errors={errors} fieldProps={{ placeholder: 'Ano' }} style={{ width: '100%' }} required={true}/>
            <TextFieldComponent name="semester" label="Semestre" control={control} errors={errors} fieldProps={{ placeholder: 'Semestre' }} style={{ width: '100%' }} required={true}/>

          </Flex>
          <SelectComponent
            name="idUniversity"
            label="Instituição de Ensino Superior"
            control={control}
            errors={errors}
            style={{ width: '100%' }}
            options={universities?.items.map(u => ({
              value: u.id ?? "",
              label: u.description ?? ""
            }))}
            itemsLoading={isLoadingUniversities}
            itemsError={errorUniversities}
            onSelect={handleUniversityChange}
            required={true}
          />
          <SelectComponent name="idFaculty" label="Unidade orgânica" control={control} errors={errors} style={{ width: '100%' }}
            options={faculties?.items.map(faculty => ({
              value: faculty.id ?? "",
              label: faculty.description ?? ""
            }))}
            onSelect={handleFacultyChange}
            required={true}
          />
          <SelectComponent name="idDepartament" label="Departamento" control={control} errors={errors} style={{ width: '100%' }}
            options={departaments?.items.map(departament => ({
              value: departament.id ?? "",
              label: departament.description ?? ""
            }))}
            onSelect={handleDepartamentChange}
            required={true}
          />
          <SelectComponent name="idCourse" label="Curso" control={control} errors={errors} style={{ width: '100%' }}
            options={courses?.items.map(course => ({
              value: course.id ?? "",
              label: course.name ?? ""
            }))}
            onSelect={(value:string)=>{
              setValue('idCourse', value)
            }}
            required={true}
          />
       <Flex gap="8">
            <RadioGroupComponent name="regime" label="Regime" control={control} errors={errors} style={{ width: '100%' }}
              options={[
                { value: "1", label: "Laboral" }
                , { value: "2", label: "Pós Laboral" }
                , { value: "3", label: "Distância" }
              ]}
              fieldProps={{
                style: { display: 'flex', flexDirection: 'row', gap: 'var(--space-4)' }
              }}
            />
          </Flex>
          <Flex gap="8">
            <TextFieldComponent name="description" label="Descrição" control={control} errors={errors} fieldProps={{ placeholder: 'Descrição da turma' }} style={{ width: '100%' }} required={true}/>
          </Flex>
          <Flex gap='4'>
            <CheckboxComponent name="useOwnRulesForAcademicPeriod" label="Usa regras próprias para o período académico?" control={control} errors={errors} required={true} />
          </Flex>
           <Flex gap='4'>
              <DateFieldComponent name="start" label='Início' control={control} errors={errors}  required={isUseOwnRulesForAcademicPeriod} style={{ width: '100%' }}/>
              <DateFieldComponent name="end" label='Fim' control={control} errors={errors}  required = {isUseOwnRulesForAcademicPeriod} style={{ width: '100%' }}/>
          </Flex>
          <Flex direction='row' gap='4' style={{ alignSelf: 'end' }}>
            <Button type='button' variant='soft' color='gray' onClick={handleBack} disabled={mutation.isLoading}>
              <ChevronLeftIcon /> Voltar
            </Button>
            <Button type="submit" disabled={mutation.isLoading} variant='surface'>
              {mutation.isLoading ? <Spinner loading> <EnterIcon /></Spinner> : <EnterIcon />}
              Actualizar
            </Button>
          </Flex>
        </Flex>
      </Card>


    </form>

  )
}
